import React, { useEffect, useState, useCallback } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import LanguageIcon from '@mui/icons-material/Language';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { 
  Brief, 
  AdPlacementTemplate, 
  ProductSearchResult,
  BriefProduct,
  WIZARD_STEPS,
  WizardStep
} from './types';
import { AUDIENCE_DEFINITIONS } from './audiences';
import { 
  PRIMARY_OBJECTIVES, 
  SECONDARY_OBJECTIVES, 
  SEASONS, 
  SEASONAL_THEMES, 
  COMMERCIAL_CONTEXTS 
} from './objectives';
import {
  fetchBrief,
  createBrief,
  updateBrief,
  fetchTemplates,
  searchProducts,
  addProduct,
  removeProduct,
  uploadAttachment,
  deleteAttachment,
  generateBriefAi,
  checkAiStatus,
  sendToClickup,
} from './briefsApi';
import { getMe } from '../../services/authService';

interface BriefWizardPageProps {
  briefId?: string;  // Om undefined = skapa ny
  onClose: () => void;
  onSaved: () => void;
}

const BriefWizardPage: React.FC<BriefWizardPageProps> = ({
  briefId,
  onClose,
  onSaved,
}) => {
  // State
  const [loading, setLoading] = useState(!!briefId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<WizardStep>('type');
  const [brief, setBrief] = useState<Partial<Brief>>({
    type: undefined,
    title: '',
    products: [],
    hasPrices: false,
    includeEan: false,
    includeChainNumber: false,
    chainNumber: '',
    attachments: [],
  });
  
  // Templates
  const [templates, setTemplates] = useState<AdPlacementTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // Custom dimensions (manuella mått)
  const [useCustomDimensions, setUseCustomDimensions] = useState(false);
  const [customDimensions, setCustomDimensions] = useState({
    width: '',
    height: '',
    unit: 'mm' as 'mm' | 'px',
    publisherName: '',  // För print
    channelName: '',    // För digital
  });
  
  // Product search
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState<ProductSearchResult[]>([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  
  // AI
  const [aiAvailable, setAiAvailable] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiResult, setAiResult] = useState<{
    briefText: string;
    strategyText?: string;
    adCopyVariants: any[];
    missingInfoQuestions: string[];
  } | null>(null);

  // Redigerbar brief-text
  const [editableBriefText, setEditableBriefText] = useState('');
  const [isEditingBrief, setIsEditingBrief] = useState(false);
  
  // Redigerbar strategi-text
  const [editableStrategyText, setEditableStrategyText] = useState('');
  const [isEditingStrategy, setIsEditingStrategy] = useState(false);
  const [includeStrategyInClickup, setIncludeStrategyInClickup] = useState(true);

  // Beställare (inloggad användare)
  const [currentUserName, setCurrentUserName] = useState<string>('');

  // ClickUp
  const [sendingToClickup, setSendingToClickup] = useState(false);
  const [clickupError, setClickupError] = useState<string | null>(null);

  // Load brief if editing
  useEffect(() => {
    if (briefId) {
      loadBrief();
    }
    checkAiStatus().then(r => setAiAvailable(r.available)).catch(() => {});
    
    // Hämta inloggad användare för beställarnamn
    getMe().then(response => {
      if (response.data?.user) {
        const user = response.data.user;
        const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
        setCurrentUserName(name);
      }
    }).catch(() => {});
  }, [briefId]);

  const loadBrief = async () => {
    if (!briefId) return;
    try {
      setLoading(true);
      const data = await fetchBrief(briefId);
      setBrief(data);
      // Hoppa till review om redan genererad
      if (data.aiOutput) {
        setCurrentStep('review');
        setAiResult({
          briefText: data.aiOutput.briefText,
          strategyText: data.aiOutput.strategyText,
          adCopyVariants: data.aiOutput.adCopyVariants || [],
          missingInfoQuestions: data.aiOutput.missingInfoQuestions || []
        });
        setEditableBriefText(data.aiOutput.briefText);
        setEditableStrategyText(data.aiOutput.strategyText || '');
        setIncludeStrategyInClickup(data.aiOutput.includeStrategyInClickup !== false);
      } else if (data.placementTemplateId) {
        setCurrentStep('products');
      } else if (data.type) {
        setCurrentStep('template');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Kunde inte ladda brief');
    } finally {
      setLoading(false);
    }
  };

  // Load templates when type is selected
  useEffect(() => {
    if (brief.type) {
      setLoadingTemplates(true);
      fetchTemplates(brief.type)
        .then(setTemplates)
        .catch(() => {})
        .finally(() => setLoadingTemplates(false));
    }
  }, [brief.type]);

  // Product search
  useEffect(() => {
    if (productSearch.length >= 2) {
      const timer = setTimeout(async () => {
        setSearchingProducts(true);
        try {
          const results = await searchProducts(productSearch);
          setProductResults(results);
        } catch {
          setProductResults([]);
        } finally {
          setSearchingProducts(false);
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setProductResults([]);
    }
  }, [productSearch]);

  // Save brief
  const saveBrief = useCallback(async (updates: Partial<Brief>) => {
    setSaving(true);
    try {
      if (brief._id) {
        const updated = await updateBrief(brief._id, updates);
        setBrief(updated);
        return updated;
      } else {
        // Skapa ny
        const created = await createBrief({
          type: updates.type as 'print' | 'digital',
          title: updates.title,
          placementTemplateId: updates.placementTemplateId
        });

        // POST /api/briefs sparar bara grundfält + ev. template-snapshot.
        // Om vi har extra data (t.ex. egna mått/placementSnapshot, målgrupper, switches) behöver vi
        // direkt uppdatera briefen så att datan inte tappas bort efter skapandet.
        const followUp: Partial<Brief> = { ...updates };
        delete (followUp as any).type;
        delete (followUp as any).title;
        delete (followUp as any).placementTemplateId;

        const hasFollowUp = Object.values(followUp).some(v => v !== undefined);
        const finalBrief = hasFollowUp ? await updateBrief(created._id, followUp) : created;

        setBrief(finalBrief);
        return finalBrief;
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Kunde inte spara');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [brief._id]);

  // Add product
  const handleAddProduct = async (product: ProductSearchResult) => {
    if (!brief._id) {
      // Spara briefen först
      const saved = await saveBrief({ ...brief, type: brief.type });
      const products = await addProduct(saved._id, { assetId: product.assetId });
      setBrief(prev => ({ ...prev, _id: saved._id, products }));
    } else {
      const products = await addProduct(brief._id, { assetId: product.assetId });
      setBrief(prev => ({ ...prev, products }));
    }
    setProductSearch('');
    setProductResults([]);
  };

  // Remove product
  const handleRemoveProduct = async (assetId: string) => {
    if (!brief._id) return;
    const products = await removeProduct(brief._id, assetId);
    setBrief(prev => ({ ...prev, products }));
  };

  // Update product price
  const handleProductPriceChange = (
    assetId: string, 
    amount: number, 
    priceUnit?: string,
    isCampaign?: boolean,
    originalPrice?: number
  ) => {
    setBrief(prev => ({
      ...prev,
      products: prev.products?.map(p => 
        p.assetId === assetId 
          ? { 
              ...p, 
              price: { 
                ...p.price,
                amount, 
                currency: priceUnit || p.price?.currency || 'kr/st', 
                type: isCampaign !== undefined ? (isCampaign ? 'campaign' : 'regular') : (p.price?.type || 'regular'),
                originalPrice: originalPrice !== undefined ? originalPrice : p.price?.originalPrice
              } 
            }
          : p
      )
    }));
  };

  // Toggle kampanjpris för produkt
  const handleToggleCampaignPrice = (assetId: string) => {
    setBrief(prev => ({
      ...prev,
      products: prev.products?.map(p => 
        p.assetId === assetId 
          ? { 
              ...p, 
              price: { 
                ...p.price,
                amount: p.price?.amount || 0,
                currency: p.price?.currency || 'kr/st',
                type: p.price?.type === 'campaign' ? 'regular' : 'campaign'
              } 
            }
          : p
      )
    }));
  };

  // Update original price
  const handleOriginalPriceChange = (assetId: string, originalPrice: number) => {
    setBrief(prev => ({
      ...prev,
      products: prev.products?.map(p => 
        p.assetId === assetId 
          ? { 
              ...p, 
              price: { 
                ...p.price,
                amount: p.price?.amount || 0,
                currency: p.price?.currency || 'kr/st',
                type: p.price?.type || 'regular',
                originalPrice
              } 
            }
          : p
      )
    }));
  };

  // Update product chain number
  const handleProductChainNumberChange = (assetId: string, chainNumber: string) => {
    setBrief(prev => ({
      ...prev,
      products: prev.products?.map(p => 
        p.assetId === assetId ? { ...p, chainNumber } : p
      )
    }));
  };

  // Toggle hero product (max 1 - när en markeras som hero blir alla andra support)
  const handleToggleHeroProduct = (assetId: string) => {
    setBrief(prev => {
      const currentProduct = prev.products?.find(p => p.assetId === assetId);
      const isCurrentlyHero = currentProduct?.role === 'hero';
      
      return {
        ...prev,
        products: prev.products?.map(p => {
          if (p.assetId === assetId) {
            // Toggle: om redan hero → support, annars hero
            return { ...p, role: isCurrentlyHero ? 'support' : 'hero' as const };
          }
          // Alla andra blir support om vi sätter en ny hero
          return isCurrentlyHero ? p : { ...p, role: 'support' as const };
        })
      };
    });
  };

  // Målgrupper (steg 4)
  const handleSelectPrimaryAudience = (audienceId: string) => {
    const def = AUDIENCE_DEFINITIONS.find(a => a.id === audienceId);
    if (!def) return;

    setBrief(prev => ({
      ...prev,
      primaryAudience: { id: def.id, label: def.label },
      // Byte av primär ska rensa ev. krockande sekundärval
      secondaryAudiences: (prev.secondaryAudiences || []).filter(a => a.id !== def.id)
    }));
  };

  const handleToggleSecondaryAudience = (audienceId: string) => {
    const def = AUDIENCE_DEFINITIONS.find(a => a.id === audienceId);
    if (!def) return;

    setBrief(prev => {
      const primaryId = prev.primaryAudience?.id;
      if (primaryId === def.id) return prev; // Primär får ej vara sekundär

      const current = prev.secondaryAudiences || [];
      const exists = current.some(a => a.id === def.id);

      if (exists) {
        return { ...prev, secondaryAudiences: current.filter(a => a.id !== def.id) };
      }

      if (current.length >= 2) {
        return prev; // max 2
      }

      return { ...prev, secondaryAudiences: [...current, { id: def.id, label: def.label }] };
    });
  };

  // Generate AI
  const handleGenerate = async () => {
    if (!brief._id) return;
    
    // Spara aktuellt state först
    await saveBrief(brief);
    
    setGenerating(true);
    setError(null);
    try {
      const result = await generateBriefAi(brief._id);
      setAiResult(result);
      setEditableBriefText(result.briefText);
      // Om användaren inte har redigerat strategin, uppdatera med ny
      // Annars behåll användarens version och uppdatera bara förslaget
      if (!editableStrategyText || editableStrategyText === brief.aiOutput?.strategyText) {
        setEditableStrategyText(result.strategyText || '');
      }
      setBrief(prev => ({ ...prev, status: 'generated' }));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Kunde inte generera brief');
    } finally {
      setGenerating(false);
    }
  };

  // Navigation
  const stepIndex = WIZARD_STEPS.findIndex(s => s.step === currentStep);
  
  const canGoNext = () => {
    switch (currentStep) {
      case 'type': return !!brief.type;
      case 'template': return !!brief.placementTemplateId || !!brief.placementSnapshot?.dimensions;
      case 'products': return (brief.products?.length || 0) > 0;
      case 'audience': return !!brief.primaryAudience?.id;
      default: return true;
    }
  };

  const goNext = async () => {
    // Autosave vid varje steg
    if (brief._id && currentStep !== 'type' && currentStep !== 'template') {
      await saveBrief(brief);
    }
    
    const nextIndex = stepIndex + 1;
    if (nextIndex < WIZARD_STEPS.length) {
      setCurrentStep(WIZARD_STEPS[nextIndex].step);
    }
  };

  const goBack = () => {
    const prevIndex = stepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(WIZARD_STEPS[prevIndex].step);
    }
  };

  const handleSelectType = async (type: 'print' | 'digital') => {
    setBrief(prev => ({ ...prev, type }));
    setCurrentStep('template');
  };

  const handleSelectTemplate = async (template: AdPlacementTemplate) => {
    setUseCustomDimensions(false);
    const updates = {
      ...brief,
      type: brief.type,
      title: `${template.publisherName || template.channelName || 'Annons'} – ${template.formatName}`,
      placementTemplateId: template._id,
      placementSnapshot: {
        displayName: template.displayName,
        publisherName: template.publisherName,
        channelName: template.channelName,
        formatName: template.formatName,
        dimensions: template.dimensions,
        deadlineInfo: template.deadlineInfo
      }
    };
    
    const saved = await saveBrief(updates as any);
    setBrief(saved);
    setCurrentStep('products');
  };

  // Hantera egna/manuella mått
  const handleSelectCustomDimensions = () => {
    setUseCustomDimensions(true);
    setBrief(prev => ({ ...prev, placementTemplateId: undefined }));
    // Sätt standard unit baserat på typ
    setCustomDimensions(prev => ({
      ...prev,
      unit: brief.type === 'print' ? 'mm' : 'px'
    }));
  };

  const handleSaveCustomDimensions = async () => {
    const width = parseInt(customDimensions.width);
    const height = parseInt(customDimensions.height);
    
    if (!width || !height) {
      setError('Ange både bredd och höjd');
      return;
    }
    
    const formatName = `${width}×${height} ${customDimensions.unit}`;
    const displayName = brief.type === 'print' 
      ? `${customDimensions.publisherName || 'Annan tidning'} – ${formatName}`
      : `${customDimensions.channelName || 'Digital kanal'} – ${formatName}`;
    
    const updates = {
      ...brief,
      type: brief.type,
      title: displayName,
      placementTemplateId: undefined, // Ingen mall vald
      placementSnapshot: {
        displayName,
        publisherName: brief.type === 'print' ? customDimensions.publisherName : undefined,
        channelName: brief.type === 'digital' ? customDimensions.channelName : undefined,
        formatName,
        dimensions: { width, height, unit: customDimensions.unit }
      }
    };
    
    const saved = await saveBrief(updates as any);
    setBrief(saved);
    setCurrentStep('products');
  };

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Skicka till ClickUp
  const handleSendToClickup = async () => {
    if (!brief._id || sendingToClickup) return;
    
    setSendingToClickup(true);
    setClickupError(null);
    
    try {
      const result = await sendToClickup(brief._id, {
        briefText: editableBriefText,
        strategyText: editableStrategyText,
        includeStrategyInClickup
      });
      // Uppdatera brief med ClickUp-info
      setBrief(prev => ({
        ...prev,
        clickupTaskId: result.taskId,
        clickupTaskUrl: result.taskUrl,
        clickupSentAt: new Date().toISOString()
      }));
    } catch (err: any) {
      const message = err?.response?.data?.message || err.message || 'Kunde inte skicka till ClickUp';
      setClickupError(message);
      
      // Om redan skickad, uppdatera state med befintlig info
      if (err?.response?.status === 409) {
        const data = err.response.data;
        setBrief(prev => ({
          ...prev,
          clickupTaskId: data.taskId,
          clickupTaskUrl: data.taskUrl
        }));
      }
    } finally {
      setSendingToClickup(false);
    }
  };

  // Bygg rubrik för briefen
  const buildBriefTitle = (): string => {
    const snap = brief.placementSnapshot;
    
    let typeLabel = brief.type === 'print' ? 'Printannons' : 'Digital annons';
    
    let mediaLabel = '';
    if (snap) {
      if (brief.type === 'print' && snap.publisherName) {
        mediaLabel = snap.publisherName;
      } else if (brief.type === 'digital' && snap.channelName) {
        mediaLabel = snap.channelName;
      } else if (snap.displayName) {
        mediaLabel = snap.displayName;
      }
      
      if (snap.dimensions) {
        const dims = `${snap.dimensions.width}×${snap.dimensions.height} ${snap.dimensions.unit || 'mm'}`;
        if (mediaLabel) {
          mediaLabel = `${mediaLabel} (${dims})`;
        } else {
          mediaLabel = dims;
        }
      }
    }
    
    if (mediaLabel) {
      return `${typeLabel} för ${mediaLabel}`;
    }
    return typeLabel;
  };

  const renderBriefText = (text: string) => {
    const lines = (text || '').split('\n');

    const isHeading = (line: string) => {
      const t = line.trim();
      if (!t) return false;
      // Rubriker från AI-prompten: egna rader i VERSALER
      return /^[A-ZÅÄÖ0-9 &/.-]+$/.test(t) && t.length <= 40;
    };

    return (
      <Box>
        {lines.map((line, idx) => {
          const trimmed = line.trim();
          if (!trimmed) {
            return <Box key={idx} sx={{ height: 10 }} />;
          }

          const heading = isHeading(line);
          const isListItem = /^[-•]\s+/.test(trimmed);

          return (
            <Typography
              key={idx}
              variant={heading ? 'subtitle2' : 'body2'}
              sx={{
                fontWeight: heading ? 800 : 400,
                mt: heading ? 1 : 0,
                color: heading ? '#1B3967' : 'text.primary',
                pl: isListItem ? 2 : 0
              }}
            >
              {line}
            </Typography>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={onClose}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6">
              {briefId ? 'Redigera brief' : 'Skapa ny brief'}
            </Typography>
            {saving && <CircularProgress size={20} />}
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* Stepper */}
        <Stepper activeStep={stepIndex} sx={{ mt: 2 }}>
          {WIZARD_STEPS.map(({ step, label }) => (
            <Step key={step}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {/* STEG 1: Typ */}
        {currentStep === 'type' && (
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
              Vad vill du skapa?
            </Typography>
            <Grid container spacing={3} justifyContent="center">
              <Grid size={{ xs: 12, sm: 6 }}>
                <Card 
                  sx={{ 
                    cursor: 'pointer', 
                    border: brief.type === 'print' ? '2px solid #1B3967' : '1px solid #e0e0e0',
                    '&:hover': { borderColor: '#1B3967' }
                  }}
                  onClick={() => handleSelectType('print')}
                >
                  <CardActionArea sx={{ p: 4, textAlign: 'center' }}>
                    <PrintIcon sx={{ fontSize: 64, color: '#1B3967', mb: 2 }} />
                    <Typography variant="h6">Printannons</Typography>
                    <Typography color="textSecondary">
                      Tidning, bilaga, trycksak
                    </Typography>
                  </CardActionArea>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    border: brief.type === 'digital' ? '2px solid #1B3967' : '1px solid #e0e0e0',
                    '&:hover': { borderColor: '#1B3967' }
                  }}
                  onClick={() => handleSelectType('digital')}
                >
                  <CardActionArea sx={{ p: 4, textAlign: 'center' }}>
                    <LanguageIcon sx={{ fontSize: 64, color: '#1B3967', mb: 2 }} />
                    <Typography variant="h6">Digital annons</Typography>
                    <Typography color="textSecondary">
                      Display, social media
                    </Typography>
                  </CardActionArea>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* STEG 2: Mall */}
        {currentStep === 'template' && (
          <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 1 }}>
              Välj annonsmall
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: 'center', mb: 4 }}>
              {brief.type === 'print' ? 'Vilken tidning och format?' : 'Vilken kanal och storlek?'}
            </Typography>
            
            {loadingTemplates ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Grid container spacing={2}>
                  {templates.map((template) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template._id}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer',
                          border: brief.placementTemplateId === template._id && !useCustomDimensions ? '2px solid #1B3967' : '1px solid #e0e0e0',
                          '&:hover': { borderColor: '#1B3967' }
                        }}
                        onClick={() => handleSelectTemplate(template)}
                      >
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {template.publisherName || template.channelName}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {template.formatName}
                          </Typography>
                          {template.dimensions && (
                            <Chip 
                              label={`${template.dimensions.width}×${template.dimensions.height} ${template.dimensions.unit}`}
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                  
                  {/* Egna mått - kort */}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        border: useCustomDimensions ? '2px solid #1B3967' : '1px solid #e0e0e0',
                        bgcolor: useCustomDimensions ? '#f5f9ff' : 'white',
                        '&:hover': { borderColor: '#1B3967' },
                        height: '100%'
                      }}
                      onClick={handleSelectCustomDimensions}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <EditIcon sx={{ fontSize: 20, color: '#1B3967' }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Egna mått
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                          Ange mått manuellt
                        </Typography>
                        <Chip 
                          label="Valfritt format"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Formulär för egna mått */}
                <Collapse in={useCustomDimensions}>
                  <Paper sx={{ mt: 3, p: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Ange mått manuellt
                    </Typography>
                    
                    <Grid container spacing={2}>
                      {/* Tidning/kanal-namn */}
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label={brief.type === 'print' ? 'Tidning/publikation (valfritt)' : 'Kanal (valfritt)'}
                          placeholder={brief.type === 'print' ? 'ex: Svensk Cater, Restaurangvärlden' : 'ex: Display, LinkedIn, Facebook'}
                          value={brief.type === 'print' ? customDimensions.publisherName : customDimensions.channelName}
                          onChange={(e) => setCustomDimensions(prev => ({
                            ...prev,
                            [brief.type === 'print' ? 'publisherName' : 'channelName']: e.target.value
                          }))}
                        />
                      </Grid>
                      
                      {/* Bredd */}
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          fullWidth
                          label="Bredd"
                          type="number"
                          value={customDimensions.width}
                          onChange={(e) => setCustomDimensions(prev => ({ ...prev, width: e.target.value }))}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">{customDimensions.unit}</InputAdornment>
                          }}
                        />
                      </Grid>
                      
                      {/* Höjd */}
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          fullWidth
                          label="Höjd"
                          type="number"
                          value={customDimensions.height}
                          onChange={(e) => setCustomDimensions(prev => ({ ...prev, height: e.target.value }))}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">{customDimensions.unit}</InputAdornment>
                          }}
                        />
                      </Grid>
                      
                      {/* Enhet */}
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          fullWidth
                          select
                          label="Enhet"
                          value={customDimensions.unit}
                          onChange={(e) => setCustomDimensions(prev => ({ ...prev, unit: e.target.value as 'mm' | 'px' }))}
                        >
                          <MenuItem value="mm">mm (millimeter)</MenuItem>
                          <MenuItem value="px">px (pixlar)</MenuItem>
                        </TextField>
                      </Grid>
                      
                      {/* Spara-knapp */}
                      <Grid size={{ xs: 12 }}>
                        <Button
                          variant="contained"
                          onClick={handleSaveCustomDimensions}
                          disabled={!customDimensions.width || !customDimensions.height}
                          sx={{ bgcolor: '#1B3967', mt: 1 }}
                        >
                          Fortsätt med dessa mått
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                </Collapse>
              </>
            )}
          </Box>
        )}

        {/* STEG 3: Produkter */}
        {currentStep === 'products' && (
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom>
              Vilka produkter ingår?
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Sök och lägg till produkter. Markera en som "Hero-produkt" om den ska lyftas fram extra mycket. Om priser ska visas, ange pris och enhet per produkt.
            </Typography>
            
            {/* Sök produkter */}
            <TextField
              fullWidth
              placeholder="Sök produkt (namn, artikelnummer eller GTIN)..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {searchingProducts ? <CircularProgress size={20} /> : <SearchIcon />}
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {/* Sökresultat */}
            {productResults.length > 0 && (
              <Paper sx={{ mb: 3, maxHeight: 300, overflow: 'auto' }}>
                {productResults.map((product) => {
                  const isAdded = brief.products?.some(p => p.assetId === product.assetId);
                  return (
                    <Box
                      key={product.assetId}
                      sx={{ 
                        p: 2, 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2,
                        borderBottom: '1px solid #eee',
                        '&:hover': { bgcolor: '#f5f5f5' }
                      }}
                    >
                      {product.previewUrl && (
                        <Box
                          component="img"
                          src={product.previewUrl}
                          sx={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 1 }}
                        />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {product.title}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {product.artikelnummer && `Art: ${product.artikelnummer}`}
                          {product.netContent && ` • ${product.netContent}`}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant={isAdded ? "outlined" : "contained"}
                        disabled={isAdded}
                        onClick={() => handleAddProduct(product)}
                        startIcon={isAdded ? <CheckCircleIcon /> : <AddIcon />}
                      >
                        {isAdded ? 'Tillagd' : 'Lägg till'}
                      </Button>
                    </Box>
                  );
                })}
              </Paper>
            )}

            {/* Valda produkter */}
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Valda produkter ({brief.products?.length || 0})
            </Typography>
            
            {/* Priser-toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={brief.hasPrices || false}
                  onChange={(e) => setBrief(prev => ({ ...prev, hasPrices: e.target.checked }))}
                />
              }
              label="Visa priser i annonsen"
              sx={{ mb: 1 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={brief.includeEan || false}
                  onChange={(e) => setBrief(prev => ({ ...prev, includeEan: e.target.checked }))}
                />
              }
              label="Visa EAN-kod i annonsen (om den finns)"
              sx={{ mb: 1 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={brief.includeChainNumber || false}
                  onChange={(e) => setBrief(prev => ({
                    ...prev,
                    includeChainNumber: e.target.checked
                  }))}
                />
              }
              label="Visa kedjenummer i annonsen (anges per produkt nedan)"
              sx={{ mb: 1 }}
            />

            {(brief.products || []).map((product) => (
              <Paper key={product.assetId} sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {product.snapshot.previewUrl && (
                    <Box
                      component="img"
                      src={product.snapshot.previewUrl}
                      sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 1 }}
                    />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {product.snapshot.title}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                      {product.snapshot.artikelnummer && `Art: ${product.snapshot.artikelnummer}`}
                      {product.snapshot.netContent && ` • ${product.snapshot.netContent}`}
                    </Typography>
                    
                    {/* Hero checkbox */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={product.role === 'hero'}
                          onChange={() => handleToggleHeroProduct(product.assetId)}
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <StarIcon sx={{ fontSize: 16, color: product.role === 'hero' ? '#1B3967' : 'text.secondary' }} />
                          <Typography variant="body2">Hero-produkt (lyfts fram mest)</Typography>
                        </Box>
                      }
                      sx={{ mr: 2 }}
                    />
                    
                    {/* Pris och kedjenummer rad */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
                      {brief.hasPrices && (
                        <>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={product.price?.type === 'campaign'}
                                onChange={() => handleToggleCampaignPrice(product.assetId)}
                                size="small"
                              />
                            }
                            label={<Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Kampanjpris</Typography>}
                            sx={{ m: 0, mr: 1 }}
                          />
                          {product.price?.type === 'campaign' && (
                            <TextField
                              size="small"
                              type="number"
                              label="Ord. pris"
                              placeholder="Valfritt"
                              value={product.price?.originalPrice || ''}
                              onChange={(e) => handleOriginalPriceChange(product.assetId, Number(e.target.value))}
                              sx={{ width: 80 }}
                            />
                          )}
                          <TextField
                            size="small"
                            type="number"
                            label={product.price?.type === 'campaign' ? 'Kampanjpris' : 'Pris'}
                            value={product.price?.amount || ''}
                            onChange={(e) => handleProductPriceChange(product.assetId, Number(e.target.value))}
                            sx={{ width: 90 }}
                          />
                          <TextField
                            size="small"
                            select
                            label="Enhet"
                            value={product.price?.currency || 'kr/st'}
                            onChange={(e) => handleProductPriceChange(product.assetId, product.price?.amount || 0, e.target.value)}
                            sx={{ width: 100 }}
                          >
                            <MenuItem value="kr/st">kr/st</MenuItem>
                            <MenuItem value="kr/kg">kr/kg</MenuItem>
                            <MenuItem value="kr/liter">kr/liter</MenuItem>
                            <MenuItem value="kr/förp">kr/förp</MenuItem>
                            <MenuItem value="kr">kr</MenuItem>
                          </TextField>
                        </>
                      )}
                      
                      {brief.includeChainNumber && (
                        <TextField
                          size="small"
                          label="Kedjenummer"
                          placeholder="ex. ICA: 445566"
                          value={product.chainNumber || ''}
                          onChange={(e) => handleProductChainNumberChange(product.assetId, e.target.value)}
                          sx={{ width: 150 }}
                        />
                      )}
                    </Box>
                  </Box>
                  
                  <IconButton onClick={() => handleRemoveProduct(product.assetId)} color="error" sx={{ mt: 0.5 }}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
        )}

        {/* STEG 4: Målgrupp */}
        {currentStep === 'audience' && (
          <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom>
              Målgrupp
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Välj primär målgrupp (obligatoriskt) och upp till 2 sekundära målgrupper. Detta påverkar tonalitet och argumentation i annonsen.
            </Typography>

            {/* Primär målgrupp */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Primär målgrupp <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Välj den viktigaste mottagaren för kommunikationen.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4 }}>
              {AUDIENCE_DEFINITIONS.map((aud) => {
                const selected = brief.primaryAudience?.id === aud.id;
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={aud.id}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        borderColor: selected ? '#1B3967' : 'divider',
                        borderWidth: selected ? 2 : 1,
                        '&:hover': { borderColor: '#1B3967' },
                        height: '100%'
                      }}
                      onClick={() => handleSelectPrimaryAudience(aud.id)}
                    >
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Radio
                          checked={selected}
                          onChange={() => handleSelectPrimaryAudience(aud.id)}
                          sx={{ pt: 0.5 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                            {aud.label}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, mb: 1 }}>
                            {aud.description}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                            Används när:
                          </Typography>
                          <Box
                            component="ul"
                            sx={{
                              mt: 0.5,
                              mb: 0,
                              pl: 2,
                              color: 'text.secondary',
                              fontSize: 12
                            }}
                          >
                            {aud.usesWhen.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            {/* Sekundär målgrupp */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Sekundär målgrupp (valfritt, max 2)
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Välj upp till två kompletterande målgrupper. Primär målgrupp kan inte väljas här.
            </Typography>

            {(() => {
              const selectedSecondary = brief.secondaryAudiences || [];
              const maxReached = selectedSecondary.length >= 2;
              const primaryId = brief.primaryAudience?.id;

              return (
                <Grid container spacing={2}>
                  {AUDIENCE_DEFINITIONS.map((aud) => {
                    const isPrimary = primaryId === aud.id;
                    const isChecked = selectedSecondary.some(a => a.id === aud.id);
                    const isDisabled = isPrimary || (!isChecked && maxReached);

                    return (
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={aud.id}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            opacity: isDisabled ? 0.6 : 1,
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            borderColor: isChecked ? '#1B3967' : 'divider',
                            borderWidth: isChecked ? 2 : 1,
                            '&:hover': { borderColor: isDisabled ? 'divider' : '#1B3967' },
                            height: '100%'
                          }}
                          onClick={() => {
                            if (!isDisabled) handleToggleSecondaryAudience(aud.id);
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Checkbox
                              checked={isChecked}
                              disabled={isDisabled}
                              onChange={() => handleToggleSecondaryAudience(aud.id)}
                              sx={{ pt: 0.5 }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                {aud.label}{isPrimary ? ' (primär)' : ''}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, mb: 1 }}>
                                {aud.description}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                                Används när:
                              </Typography>
                              <Box
                                component="ul"
                                sx={{
                                  mt: 0.5,
                                  mb: 0,
                                  pl: 2,
                                  color: 'text.secondary',
                                  fontSize: 12
                                }}
                              >
                                {aud.usesWhen.map((item) => (
                                  <li key={item}>{item}</li>
                                ))}
                              </Box>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              );
            })()}
          </Box>
        )}

        {/* STEG 5: Syfte */}
        {currentStep === 'objective' && (
          <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom>
              Syfte
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Välj vad annonsen ska uppnå. Detta hjälper AI att skapa en bättre brief.
            </Typography>
            
            <Grid container spacing={2}>
              {/* Kolumn 1: Primärt syfte */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Primärt syfte (välj 1)
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                    Vad är det viktigaste målet?
                  </Typography>
                  {PRIMARY_OBJECTIVES.map((obj) => (
                    <FormControlLabel
                      key={obj.id}
                      control={
                        <Radio
                          checked={brief.objectivePrimary === obj.label}
                          onChange={() => setBrief(prev => ({ ...prev, objectivePrimary: obj.label }))}
                          size="small"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>{obj.label}</Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>{obj.description}</Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', m: 0, mb: 0.5, width: '100%', display: 'flex' }}
                    />
                  ))}
                  <TextField
                    fullWidth
                    size="small"
                    label="Egen text"
                    placeholder="Förtydliga..."
                    value={brief.objectivePrimary && !PRIMARY_OBJECTIVES.some(o => o.label === brief.objectivePrimary) ? brief.objectivePrimary : ''}
                    onChange={(e) => setBrief(prev => ({ ...prev, objectivePrimary: e.target.value }))}
                    sx={{ mt: 1 }}
                  />
                </Paper>
              </Grid>

              {/* Kolumn 2: Delmål */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Delmål (välj flera)
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                    Ytterligare mål att uppnå
                  </Typography>
                  {SECONDARY_OBJECTIVES.map((obj) => {
                    const currentGoals = (brief.objectiveSecondary || '').split(', ').filter(Boolean);
                    const isSelected = currentGoals.includes(obj.label);
                    return (
                      <FormControlLabel
                        key={obj.id}
                        control={
                          <Checkbox
                            checked={isSelected}
                            onChange={() => {
                              const newGoals = isSelected
                                ? currentGoals.filter(g => g !== obj.label)
                                : [...currentGoals, obj.label];
                              setBrief(prev => ({ ...prev, objectiveSecondary: newGoals.join(', ') }));
                            }}
                            size="small"
                          />
                        }
                        label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{obj.label}</Typography>}
                        sx={{ m: 0, mb: 0.25, display: 'flex' }}
                      />
                    );
                  })}
                  <TextField
                    fullWidth
                    size="small"
                    label="Egna delmål"
                    placeholder="Lägg till..."
                    sx={{ mt: 1 }}
                    onBlur={(e) => {
                      if (e.target.value.trim()) {
                        const currentGoals = (brief.objectiveSecondary || '').split(', ').filter(Boolean);
                        if (!currentGoals.includes(e.target.value.trim())) {
                          setBrief(prev => ({ 
                            ...prev, 
                            objectiveSecondary: [...currentGoals, e.target.value.trim()].join(', ') 
                          }));
                        }
                        e.target.value = '';
                      }
                    }}
                  />
                </Paper>
              </Grid>

              {/* Kolumn 3: Säsong/Tillfälle */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Säsong / Tillfälle
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                    Relevant kontext för annonsen
                  </Typography>
                  
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>Säsong</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5, mt: 0.5 }}>
                    {SEASONS.map((season) => {
                      const currentSeasons = (brief.campaignSeason || '').split(', ').filter(Boolean);
                      const isSelected = currentSeasons.includes(season.label);
                      return (
                        <Chip
                          key={season.id}
                          label={season.label}
                          onClick={() => {
                            const newSeasons = isSelected
                              ? currentSeasons.filter(s => s !== season.label)
                              : [...currentSeasons, season.label];
                            setBrief(prev => ({ ...prev, campaignSeason: newSeasons.join(', ') }));
                          }}
                          color={isSelected ? 'primary' : 'default'}
                          variant={isSelected ? 'filled' : 'outlined'}
                          size="small"
                          sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                        />
                      );
                    })}
                  </Box>
                  
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>Teman</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5, mt: 0.5 }}>
                    {SEASONAL_THEMES.map((theme) => {
                      const currentSeasons = (brief.campaignSeason || '').split(', ').filter(Boolean);
                      const isSelected = currentSeasons.includes(theme.label);
                      return (
                        <Chip
                          key={theme.id}
                          label={theme.label}
                          onClick={() => {
                            const newSeasons = isSelected
                              ? currentSeasons.filter(s => s !== theme.label)
                              : [...currentSeasons, theme.label];
                            setBrief(prev => ({ ...prev, campaignSeason: newSeasons.join(', ') }));
                          }}
                          color={isSelected ? 'primary' : 'default'}
                          variant={isSelected ? 'filled' : 'outlined'}
                          size="small"
                          sx={{ cursor: 'pointer', fontSize: '0.65rem' }}
                        />
                      );
                    })}
                  </Box>
                  
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>Kommersiell kontext</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, mt: 0.5 }}>
                    {COMMERCIAL_CONTEXTS.map((ctx) => {
                      const currentSeasons = (brief.campaignSeason || '').split(', ').filter(Boolean);
                      const isSelected = currentSeasons.includes(ctx.label);
                      return (
                        <Chip
                          key={ctx.id}
                          label={ctx.label}
                          onClick={() => {
                            const newSeasons = isSelected
                              ? currentSeasons.filter(s => s !== ctx.label)
                              : [...currentSeasons, ctx.label];
                            setBrief(prev => ({ ...prev, campaignSeason: newSeasons.join(', ') }));
                          }}
                          color={isSelected ? 'primary' : 'default'}
                          variant={isSelected ? 'filled' : 'outlined'}
                          size="small"
                          sx={{ cursor: 'pointer', fontSize: '0.65rem' }}
                        />
                      );
                    })}
                  </Box>
                  
                  <TextField
                    fullWidth
                    size="small"
                    label="Egen text"
                    placeholder="Lägg till..."
                    sx={{ mt: 1 }}
                    onBlur={(e) => {
                      if (e.target.value.trim()) {
                        const currentSeasons = (brief.campaignSeason || '').split(', ').filter(Boolean);
                        if (!currentSeasons.includes(e.target.value.trim())) {
                          setBrief(prev => ({ 
                            ...prev, 
                            campaignSeason: [...currentSeasons, e.target.value.trim()].join(', ') 
                          }));
                        }
                        e.target.value = '';
                      }
                    }}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* STEG 6: Teknisk info */}
        {currentStep === 'technical' && (
          <Box sx={{ maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom>
              Teknisk information
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Fyll i tekniska detaljer och praktisk information för produktion.
            </Typography>
            
            <TextField
              fullWidth
              label="Vilken kedja?"
              placeholder="ex: ICA, Coop, Willys, Hemköp..."
              value={brief.chainNumber || ''}
              onChange={(e) => setBrief(prev => ({ ...prev, chainNumber: e.target.value }))}
              sx={{ mb: 3 }}
              helperText="Ange vilken kedja annonsen är för"
            />
            
            <TextField
              fullWidth
              label="Särskilda tekniska krav"
              placeholder="ex: 4-färg, bestruket papper"
              value={brief.technicalRequirements || ''}
              onChange={(e) => setBrief(prev => ({ ...prev, technicalRequirements: e.target.value }))}
              sx={{ mb: 3 }}
            />
            
            <TextField
              fullWidth
              label="Övrigt"
              placeholder="ex: levereras till nissestryck@tryck"
              value={brief.otherNotes || ''}
              onChange={(e) => setBrief(prev => ({ ...prev, otherNotes: e.target.value }))}
              sx={{ mb: 3 }}
              multiline
              rows={2}
            />
            
            <TextField
              fullWidth
              label="Deadline"
              placeholder="ex: Materialdag, senaste inlämning"
              value={brief.deadlineText || ''}
              onChange={(e) => setBrief(prev => ({ ...prev, deadlineText: e.target.value }))}
              sx={{ mb: 3 }}
            />
            
            {/* Bilagor */}
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Bilagor (PDF med teknisk info)
            </Typography>
            
            <Button
              variant="outlined"
              component="label"
              startIcon={<AttachFileIcon />}
              sx={{ mb: 2 }}
            >
              Ladda upp PDF
              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file && brief._id) {
                    try {
                      const attachments = await uploadAttachment(brief._id, file);
                      setBrief(prev => ({ ...prev, attachments }));
                    } catch (err: any) {
                      alert('Kunde inte ladda upp fil');
                    }
                  }
                }}
              />
            </Button>
            
            {(brief.attachments || []).map((att: any) => (
              <Chip
                key={att._id}
                label={att.fileName}
                onDelete={async () => {
                  if (brief._id && att._id) {
                    const attachments = await deleteAttachment(brief._id, att._id);
                    setBrief(prev => ({ ...prev, attachments }));
                  }
                }}
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>
        )}

        {/* STEG 7: Granska & Generera */}
        {currentStep === 'review' && (
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom>
              Granska & Generera
            </Typography>
            
            {/* Sammanfattning (Accordion - default infälld) */}
            <Accordion defaultExpanded={false} sx={{ mb: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Sammanfattning
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {brief.type === 'print' ? 'Print' : 'Digital'} • {brief.placementSnapshot?.displayName || '-'} • {brief.products?.length || 0} produkter
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {/* Rad 1: Typ och Mall */}
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="caption" color="textSecondary">Typ</Typography>
                    <Typography variant="body2">{brief.type === 'print' ? 'Print' : 'Digital'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="caption" color="textSecondary">Mall/Placering</Typography>
                    <Typography variant="body2">{brief.placementSnapshot?.displayName || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="caption" color="textSecondary">Format</Typography>
                    <Typography variant="body2">
                      {brief.placementSnapshot?.dimensions 
                        ? `${brief.placementSnapshot.dimensions.width}×${brief.placementSnapshot.dimensions.height} ${brief.placementSnapshot.dimensions.unit || 'mm'}`
                        : '-'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="caption" color="textSecondary">Kedja</Typography>
                    <Typography variant="body2">{brief.chainNumber || '-'}</Typography>
                  </Grid>

                  {/* Rad 2: Produkter */}
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 0.5 }} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="textSecondary">Produkter ({brief.products?.length || 0} st)</Typography>
                    {(brief.products || []).length > 0 ? (
                      <Box sx={{ mt: 0.5 }}>
                        {brief.products?.map((p) => (
                          <Typography key={p.assetId} variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem' }}>
                            {p.role === 'hero' && <StarIcon sx={{ fontSize: 12, color: '#1B3967' }} />}
                            {p.snapshot.title}
                            {p.price?.amount && ` – ${p.price.amount} ${p.price.currency || 'kr'}`}
                            {brief.includeChainNumber && p.chainNumber && ` (${p.chainNumber})`}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2">-</Typography>
                    )}
                  </Grid>

                  {/* Rad 3: Målgrupp + Syfte (kompakt) */}
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 0.5 }} />
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="caption" color="textSecondary">Målgrupp</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{brief.primaryAudience?.label || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="caption" color="textSecondary">Huvudsyfte</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{brief.objectivePrimary || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="caption" color="textSecondary">Deadline</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{brief.deadlineText || brief.placementSnapshot?.deadlineInfo || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="caption" color="textSecondary">Bilagor</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{brief.attachments?.length || 0} st</Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* AI-generering */}
            {!aiResult ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AutoAwesomeIcon sx={{ fontSize: 64, color: '#1B3967', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Redo att generera brief
                </Typography>
                <Typography color="textSecondary" sx={{ mb: 3 }}>
                  AI kommer skriva en kortfattad brief och förslag på annonstext baserat på din information.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGenerate}
                  disabled={generating || !aiAvailable}
                  startIcon={generating ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                  sx={{ bgcolor: '#1B3967' }}
                >
                  {generating ? 'Genererar...' : 'Generera brief'}
                </Button>
                {!aiAvailable && (
                  <Typography color="error" sx={{ mt: 2 }}>
                    AI-tjänsten är inte tillgänglig. Kontrollera API-nyckel.
                  </Typography>
                )}
              </Box>
            ) : (
              <Box>
                {/* Saknade frågor */}
                {aiResult.missingInfoQuestions.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      AI behöver mer information:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {aiResult.missingInfoQuestions.map((q, i) => (
                        <li key={i}>{q}</li>
                      ))}
                    </ul>
                  </Alert>
                )}

                {/* Brief-text */}
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Brief till studio
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {!isEditingBrief ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => setIsEditingBrief(true)}
                        >
                          Redigera
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => setIsEditingBrief(false)}
                          sx={{ bgcolor: '#1B3967' }}
                        >
                          Klar
                        </Button>
                      )}
                      <IconButton onClick={() => copyToClipboard(editableBriefText)} size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  {/* Rubrik */}
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1B3967', mb: 1 }}>
                    {buildBriefTitle()}
                  </Typography>
                  
                  {/* Beställare */}
                  {currentUserName && (
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      <strong>Beställare:</strong> {currentUserName}
                    </Typography>
                  )}
                  
                  {/* Strategisk riktning (AI-analys) */}
                  {editableStrategyText && (
                    <Box sx={{ 
                      mb: 3, 
                      p: 2, 
                      bgcolor: '#f0f4f8', 
                      borderRadius: 1,
                      border: '1px solid #d0d9e3'
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1B3967' }}>
                            STRATEGISK RIKTNING (AI-analys)
                          </Typography>
                          <Chip 
                            label="AI-genererad" 
                            size="small" 
                            sx={{ 
                              bgcolor: '#1B3967', 
                              color: 'white', 
                              fontSize: '0.65rem', 
                              height: 18 
                            }} 
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          {!isEditingStrategy ? (
                            <IconButton size="small" onClick={() => setIsEditingStrategy(true)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => setIsEditingStrategy(false)}
                              sx={{ bgcolor: '#1B3967', fontSize: '0.7rem', py: 0.25, px: 1 }}
                            >
                              Klar
                            </Button>
                          )}
                        </Box>
                      </Box>
                      
                      {isEditingStrategy ? (
                        <TextField
                          multiline
                          fullWidth
                          minRows={4}
                          value={editableStrategyText}
                          onChange={(e) => setEditableStrategyText(e.target.value)}
                          variant="outlined"
                          size="small"
                          placeholder="Strategisk analys..."
                        />
                      ) : (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                          {editableStrategyText}
                        </Typography>
                      )}
                      
                      <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={includeStrategyInClickup}
                              onChange={(e) => setIncludeStrategyInClickup(e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="caption" color="textSecondary">
                              Inkludera i ClickUp
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      </Box>
                    </Box>
                  )}
                  
                  {/* Redigerbar eller statisk brief-text */}
                  {isEditingBrief ? (
                    <TextField
                      multiline
                      fullWidth
                      minRows={10}
                      value={editableBriefText}
                      onChange={(e) => setEditableBriefText(e.target.value)}
                      variant="outlined"
                      placeholder="Brief-text..."
                    />
                  ) : (
                    renderBriefText(editableBriefText)
                  )}
                  
                  {/* Skicka till ClickUp */}
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                    {brief.clickupTaskId ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: 'success.main' }} />
                        <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                          Skickad till ClickUp
                        </Typography>
                        {brief.clickupTaskUrl && (
                          <Button
                            size="small"
                            variant="text"
                            endIcon={<OpenInNewIcon />}
                            href={brief.clickupTaskUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Öppna i ClickUp
                          </Button>
                        )}
                      </Box>
                    ) : (
                      <Box>
                        <Button
                          variant="contained"
                          startIcon={sendingToClickup ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                          onClick={handleSendToClickup}
                          disabled={sendingToClickup}
                          sx={{ bgcolor: '#1B3967' }}
                        >
                          {sendingToClickup ? 'Skickar...' : 'Skicka till ClickUp'}
                        </Button>
                        {clickupError && (
                          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setClickupError(null)}>
                            {clickupError}
                          </Alert>
                        )}
                      </Box>
                    )}
                  </Box>
                </Paper>

                {/* Regenerera */}
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={handleGenerate}
                    disabled={generating}
                    startIcon={generating ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                  >
                    Generera om
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Footer med navigation */}
      <Paper elevation={0} sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 800, mx: 'auto' }}>
          <Button
            onClick={goBack}
            disabled={stepIndex === 0}
            startIcon={<ArrowBackIcon />}
          >
            Tillbaka
          </Button>
          
          {currentStep !== 'review' ? (
            <Button
              variant="contained"
              onClick={goNext}
              disabled={!canGoNext() || saving}
              endIcon={saving ? <CircularProgress size={20} /> : <ArrowForwardIcon />}
              sx={{ bgcolor: '#1B3967' }}
            >
              Nästa
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => {
                onSaved();
                onClose();
              }}
              sx={{ bgcolor: '#1B3967' }}
            >
              Klar
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default BriefWizardPage;
